/*
 * Copyright (c) CIRROM 2024.
 */

import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getAllAccountNames } from "../../../api/datasources";
import {
  DataLoaderListOption,
  getAvailableListOptions,
} from "../../../api/datasystems";
import CircularProgress from "@mui/material/CircularProgress";
import { Alert } from "@mui/material";

/**
 * Custom hook to fetch account names with loading, error states, and request cancellation
 */
const useAccountNames = (integratedSystemId: number) => {
  const [accountNames, setAccountNames] = useState<
    { crm_account_id: string; name: string }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!integratedSystemId) {
      setAccountNames([]);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    getAllAccountNames(integratedSystemId)
      .then((response) => {
        if (!controller.signal.aborted) {
          setAccountNames(response);
          setError(null);
        }
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          console.error("Error loading account names:", err);
          setError("Failed to load accounts. Please try again.");
          setAccountNames([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [integratedSystemId]);

  return { accountNames, loading, error };
};

type AccountOption = { id: string; label: string; companyName: string };

/**
 * A component that provides an autocomplete feature for account names.
 *
 * @param {Object} props - The props object containing the following properties:
 *   - integratedSystemId: The ID of the integrated system.
 *   - selectedValue: The currently selected account name (optional).
 *   - onSelectionChanged: A callback function to be called when the selected account name changes.
 *   - onMultiSelectionChanged: A callback function for multi-select mode.
 *   - multiple: Whether to enable multi-select mode.
 *
 * @returns {JSX.Element} - The rendered Autocomplete component.
 */
export const AccountNameAutocomplete = (props: {
  integratedSystemId: number;
  selectedValue?: string;
  onSelectionChanged(account: { crm_account_id: string; name: string }): void;
  onMultiSelectionChanged?(
    accounts: { crm_account_id: string; name: string }[]
  ): void;
  multiple?: boolean;
}) => {
  const { accountNames, loading, error } = useAccountNames(
    props.integratedSystemId
  );

  const [inputValue, setInputValue] = useState("");
  const [value, setValue] = useState<AccountOption | AccountOption[] | null>(
    props.multiple ? [] : null
  );

  // Compute filtered options using useMemo for performance
  const filteredOptions = useMemo(() => {
    const searchTerm = inputValue.trim().toLowerCase();

    if (!searchTerm) {
      return [];
    }

    return accountNames
      .filter((account) => {
        const name = account?.name?.toLowerCase();
        return name && name.includes(searchTerm);
      })
      .sort((a, b) => {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();

        // Exact match comes first
        if (nameA === searchTerm && nameB !== searchTerm) return -1;
        if (nameB === searchTerm && nameA !== searchTerm) return 1;

        // Starts with search term comes second
        const aStarts = nameA.startsWith(searchTerm);
        const bStarts = nameB.startsWith(searchTerm);
        if (aStarts && !bStarts) return -1;
        if (bStarts && !aStarts) return 1;

        // Otherwise maintain alphabetical order
        return nameA.localeCompare(nameB);
      })
      .slice(0, 20)
      .map((account) => ({
        id: account.crm_account_id,
        label: `${account.name} (${account.crm_account_id})`,
        companyName: account.name,
      }));
  }, [inputValue, accountNames]);

  // Initialize value from selectedValue prop
  useEffect(() => {
    if (props.selectedValue && !props.multiple) {
      // Find the account in accountNames to get the proper structure
      const selectedAccount = accountNames.find(
        (acc) =>
          acc.name === props.selectedValue ||
          acc.crm_account_id === props.selectedValue
      );

      if (selectedAccount) {
        setValue({
          id: selectedAccount.crm_account_id,
          label: `${selectedAccount.name} (${selectedAccount.crm_account_id})`,
          companyName: selectedAccount.name,
        });
      } else {
        // Fallback if account not found in list yet
        setValue({
          id: props.selectedValue,
          label: props.selectedValue,
          companyName: props.selectedValue,
        });
      }
    }
  }, [props.selectedValue, props.multiple, accountNames]);

  // Reset value when multiple prop changes to ensure correct type
  // Use a ref to track if this is the initial mount
  const prevMultipleRef = useRef(props.multiple);
  useEffect(() => {
    // Only reset if multiple actually changed (not on initial mount)
    if (prevMultipleRef.current !== props.multiple) {
      setValue(props.multiple ? [] : null);
      prevMultipleRef.current = props.multiple;
    }
  }, [props.multiple]);

  return (
    <>
      {error && (
        <Alert severity="error" style={{ marginBottom: "8px" }}>
          {error}
        </Alert>
      )}
      <Autocomplete
        data-testid="company_name_autocomplete"
        style={{ height: "25px", padding: "0px", margin: "0px" }}
        multiple={props.multiple || false}
        fullWidth
        filterOptions={(x) => x}
        options={filteredOptions}
        loading={loading}
        noOptionsText={
          loading
            ? "Loading..."
            : inputValue.trim()
            ? "No accounts found"
            : "Start typing to search"
        }
        onInputChange={(_event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        value={value || (props.multiple ? [] : null)}
        onChange={(_event, newValue) => {
          setValue(newValue as AccountOption | AccountOption[] | null);

          if (Array.isArray(newValue)) {
            // Multi-select mode: call onMultiSelectionChanged
            const allValues = newValue.map((item) => ({
              name: item.companyName,
              crm_account_id: item.id,
            }));
            props.onMultiSelectionChanged?.(allValues);
          } else if (newValue) {
            // Single-select mode: call onSelectionChanged
            props.onSelectionChanged({
              name: newValue.companyName,
              crm_account_id: newValue.id,
            });
          }
        }}
        renderTags={(tagValue, getTagProps) => {
          if (!Array.isArray(tagValue) || tagValue.length === 0) return null;

          const items = tagValue as AccountOption[];

          if (items.length === 1) {
            return (
              <Chip
                label={items[0].companyName}
                size="small"
                {...getTagProps({ index: 0 })}
              />
            );
          }
          return (
            <Chip
              label={`${items.length} items selected`}
              size="small"
              color="primary"
            />
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            required
            fullWidth
            variant="standard"
            size="small"
            margin="none"
            inputProps={{
              ...params.inputProps,
              "aria-label": "Company Name",
              style: { ...params.inputProps.style, height: "25px", padding: 0 },
            }}
            InputProps={{
              ...params.InputProps,
              style: { minHeight: "25px" },
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </>
  );
};

/**
 * Represents an autocomplete component for selecting a value from a list of names.
 * @param {number} props.integratedSystemId - The ID of the integrated system.
 * @param {string} [props.selectedValue] - The initially selected value.
 * @param {Function} props.onSelectionChanged - The callback function triggered when the selection changes.
 * @returns {JSX.Element} The autocomplete component.
 */
export const ListNameAutocomplete = (props: {
  integratedSystemId: number;
  selectedValue?: string;
  onSelectionChanged(accountName: string, label?: string): void;
}) => {
  const [dataLoderList, setDataLoaderLists] = useState<DataLoaderListOption[]>(
    []
  );
  useEffect(() => {
    let isMounted = true;
    if (props.integratedSystemId) {
      getAvailableListOptions(null, null, props.integratedSystemId)
        .then((response) => {
          if (isMounted) {
            setDataLoaderLists(response);
          }
        })
        .catch((err) => console.log(err));
    }
    return () => {
      isMounted = false;
    };
  }, [props.integratedSystemId]);
  // @ts-ignore
  return (
    <Autocomplete
      data-testid={"company_name_autocomplete"}
      style={{ height: "25px", padding: "0px", margin: "0px" }}
      fullWidth
      // @ts-ignore
      options={dataLoderList.map((entry) => {
        return { id: entry.list_id, label: entry.list_name };
      })}
      onChange={(_event, value) => {
        // @ts-ignore
        props.onSelectionChanged(value.id, value.label);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          required
          fullWidth
          variant={"standard"}
          size={"small"}
          margin={"none"}
        />
      )}
      defaultValue={props.selectedValue}
    />
  );
};
